import { addSignalAttribute, identify, sendEvent } from '@dash0/sdk-web';

import {
  ATTR_APP_ORG,
  ATTR_APP_PLAN,
  WEB_EVENT_TITLES,
  type WebEvent,
} from './attributes';
import { isDash0Enabled } from './dash0-enabled';

type EventAttributes = Readonly<Record<string, string | number | boolean>>;

export interface IdentifyInput {
  readonly userId: string;
  /** `'free' | 'paid'` — stamped on every subsequent web signal. */
  readonly plan: string;
  readonly roles?: readonly string[];
  /** GitHub org login, when the user is acting in an org context. */
  readonly org?: string;
}

/**
 * Emit a custom product-analytics event to Dash0 web events. Guarded so it no-ops
 * when Dash0 is not configured (local dev / CI / e2e), mirroring the init guard in
 * `instrumentation-client`. Telemetry is best-effort: a `sendEvent` failure is
 * swallowed so it can never degrade a successful user journey (call sites emit
 * inside their own success paths).
 */
export function track(event: WebEvent, attributes?: EventAttributes): void {
  if (!isDash0Enabled()) return;
  try {
    sendEvent(event, {
      title: WEB_EVENT_TITLES.get(event) ?? event,
      ...(attributes === undefined ? {} : { attributes }),
    });
  } catch {
    // Analytics must never break the app — drop the event on SDK/transport error.
  }
}

/**
 * Associate the current user with subsequent web signals and stamp the plan tier
 * as a signal attribute, so every event, page view, and RUM record carries
 * identity and `app.plan` — the cross-reference key against backend spans. PII is
 * limited to the opaque Clerk user id, roles, and plan. Errors are swallowed for
 * the same best-effort reason as {@link track}.
 */
export function identifyUser(input: IdentifyInput): void {
  if (!isDash0Enabled()) return;
  try {
    identify(
      input.userId,
      input.roles ? { roles: [...input.roles] } : undefined,
    );
    addSignalAttribute(ATTR_APP_PLAN, input.plan);
    if (input.org !== undefined) addSignalAttribute(ATTR_APP_ORG, input.org);
  } catch {
    // Identity enrichment is best-effort; never break the app on SDK error.
  }
}
